import React, { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';

// Shown when the app needs location permission but doesn't have it.
// First tap: requests permission from a real user-gesture context.
// If denied again: shows instructions + opens Android app settings.
const LocationPermissionBanner = () => {
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState('initial'); // initial | denied-twice
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    const onNeeded = () => setOpen(true);
    window.addEventListener('locationPermissionNeeded', onNeeded);
    return () => window.removeEventListener('locationPermissionNeeded', onNeeded);
  }, []);

  if (!open || !Capacitor.isNativePlatform?.()) return null;

  const handleEnable = async () => {
    setRequesting(true);
    try {
      const result = await Geolocation.requestPermissions({
        permissions: ['location', 'coarseLocation'],
      });
      console.log('[banner] requestPermissions result:', result);
      const granted = result.location === 'granted' || result.coarseLocation === 'granted';
      if (granted) {
        setOpen(false);
        // Re-trigger the get-location flow now that permission exists
        try {
          const pos = await Geolocation.getCurrentPosition({
            enableHighAccuracy: true,
            timeout: 15000,
          });
          if (window.leafletMap) {
            window.leafletMap.setView([pos.coords.latitude, pos.coords.longitude], 17);
          }
          if (window._setUserLocationFromBanner) {
            window._setUserLocationFromBanner({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            });
          }
        } catch (e) {
          console.warn('[banner] post-grant getCurrentPosition failed:', e?.message || e);
        }
      } else {
        setStage('denied-twice');
      }
    } catch (e) {
      console.warn('[banner] request error:', e?.message || e);
      setStage('denied-twice');
    } finally {
      setRequesting(false);
    }
  };

  const handleOpenSettings = () => {
    // Best-effort open of the app's Android settings page.
    // Capacitor doesn't expose a native settings-deep-link; we use the
    // package: scheme which Android resolves to App Info for our package.
    try {
      window.location.href = 'package:community.linksy.app';
    } catch (_) {
      try { window.open('package:community.linksy.app', '_system'); } catch (_) {}
    }
    setOpen(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        left: '12px',
        right: '12px',
        bottom: 'calc(var(--footer-height, 64px) + 12px)',
        background: '#fff',
        borderRadius: '14px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        padding: '16px',
        zIndex: 9999,
        fontFamily: 'Outfit, sans-serif',
        border: '1px solid #eee',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <div
          style={{
            width: '36px', height: '36px', borderRadius: '50%',
            background: '#e8f0fe', display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0, fontSize: '18px',
          }}
        >📍</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {stage === 'initial' ? (
            <>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#202124', marginBottom: '4px' }}>
                Enable location
              </div>
              <div style={{ fontSize: '12px', color: '#5f6368', marginBottom: '12px', lineHeight: 1.4 }}>
                Linksy needs your location to show nearby events and place your avatar on the map.
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '14px', fontWeight: 600, color: '#202124', marginBottom: '4px' }}>
                Open Settings
              </div>
              <div style={{ fontSize: '12px', color: '#5f6368', marginBottom: '12px', lineHeight: 1.4 }}>
                Location is blocked. Tap below, then under <strong>Permissions → Location</strong> choose <strong>Allow only while using the app</strong>.
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setOpen(false)}
              style={{
                flex: 1,
                padding: '10px',
                background: '#f1f3f4',
                color: '#5f6368',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >Not now</button>
            {stage === 'initial' ? (
              <button
                onClick={handleEnable}
                disabled={requesting}
                style={{
                  flex: 2,
                  padding: '10px',
                  background: requesting ? '#9ca3af' : '#1a73e8',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: requesting ? 'default' : 'pointer',
                }}
              >{requesting ? 'Requesting…' : 'Enable location'}</button>
            ) : (
              <button
                onClick={handleOpenSettings}
                style={{
                  flex: 2,
                  padding: '10px',
                  background: '#1a73e8',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >Open Settings</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPermissionBanner;
